import React from 'react';
import BoldText from './BoldText';
import { Link } from 'react-router-dom';
import { Phone, Mail, Calendar, FileText, MessageSquare, Activity, Target, Pencil, Trash2, Sparkles, Smartphone, Mic, Globe } from 'lucide-react';
import './ActivityTimeline.css';

// The one timeline. The Activities page and the account page both render
// it: entries grouped by day, each with its type, who it was with, the
// approved text, and — for a captured note — where it came from and the
// words as they were said are stored with the entry but not shown.

const ICONS = { Call: Phone, Email: Mail, Meeting: Calendar, Note: FileText, Text: MessageSquare };

const SOURCES = {
  web: { label: 'web note', Icon: Globe },
  ios: { label: 'iPhone', Icon: Smartphone },
  siri: { label: 'Siri', Icon: Mic },
};

const dayKey = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function dayLabel(key, now = new Date()) {
  if (key === 'unknown') return 'Undated';
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const todayKey = dayKey(now.toISOString());
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (key === todayKey) return 'Today';
  if (key === dayKey(yesterday.toISOString())) return 'Yesterday';
  const sameYear = y === now.getFullYear();
  return date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: sameYear ? undefined : 'numeric' });
}

const timeOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

export default function ActivityTimeline({
  activities,
  groupByDay = true,
  showAccount = true,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
  renderExtra,
}) {
  if (!activities || activities.length === 0) return null;

  const groups = [];
  if (groupByDay) {
    let current = null;
    for (const a of activities) {
      const key = dayKey(a.activity_date);
      if (!current || current.key !== key) { current = { key, items: [] }; groups.push(current); }
      current.items.push(a);
    }
  } else {
    groups.push({ key: null, items: activities });
  }

  const renderItem = (activity) => {
    const Icon = ICONS[activity.type] || Activity;
    const source = activity.source ? SOURCES[activity.source] : null;
    return (
      <li key={activity.id} className={`tl-item tl-${String(activity.type || '').toLowerCase()}`}>
        <div className="tl-icon" aria-hidden="true"><Icon size={14} /></div>
        <div className="tl-body">
          <div className="tl-head">
            <span className="tl-type">{activity.type}</span>
            <span className="tl-time">{groupByDay ? timeOf(activity.activity_date) : new Date(activity.activity_date).toLocaleString()}</span>
            {showAccount && activity.account_id && (
              <Link to={`/accounts/${activity.account_id}`} className="tl-account">{activity.account_name}</Link>
            )}
            {activity.contact_name && (
              <span className="tl-with">with {activity.contact_id ? <Link to={`/contacts/${activity.contact_id}`}>{activity.contact_name}</Link> : activity.contact_name}</span>
            )}
            {activity.opportunity_name && (
              <Link to={`/opportunities/${activity.opportunity_id}`} className="tl-deal"><Target size={12} aria-hidden="true" /> {activity.opportunity_name}</Link>
            )}
            {source && (
              <span className="tl-source" title={`Captured as a ${source.label}${activity.ai_model ? `, drafted by ${activity.ai_model}` : ''}`}>
                <Sparkles size={11} aria-hidden="true" /> <source.Icon size={11} aria-hidden="true" /> {source.label}
              </span>
            )}
          </div>
          {activity.description && <p className="tl-text"><BoldText text={activity.description} /></p>}
          <div className="tl-foot">
            <span>by {activity.logged_by_name}</span>
            {activity.updated_by_name && <span className="tl-edited">· edited by {activity.updated_by_name}{activity.updated_at ? ` on ${new Date(activity.updated_at).toLocaleDateString()}` : ''}</span>}
            {canEdit && onEdit && (
              <button type="button" className="btn btn-icon btn-sm" onClick={() => onEdit(activity)} aria-label="Edit activity" title="Edit"><Pencil size={13} /></button>
            )}
            {canDelete && onDelete && (
              <button type="button" className="btn btn-icon btn-sm" onClick={() => onDelete(activity)} aria-label="Delete activity" title="Delete"><Trash2 size={13} /></button>
            )}
          </div>
          {renderExtra && renderExtra(activity)}
        </div>
      </li>
    );
  };

  return (
    <div className="tl">
      {groups.map((group) => (
        <section key={group.key || 'all'} className="tl-group">
          {group.key && <h4 className="tl-day">{dayLabel(group.key)}</h4>}
          <ol className="tl-list">{group.items.map(renderItem)}</ol>
        </section>
      ))}
    </div>
  );
}
